/// <reference types="vite/client" />
import { getLocale, m } from '@aimfora/i18n'
import { Toaster } from '@aimfora/ui'
import { PostHogErrorBoundary, PostHogProvider, usePostHog } from '@posthog/react'
import { createRootRouteWithContext, HeadContent, Outlet, Scripts } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { type ReactNode, useEffect, useRef } from 'react'
import { ConsentManager } from '~/components/consent-manager'
import { NewVersionNotice } from '~/components/new-version'
import { getPublicConfig, getSessionUser } from '~/functions/session.functions'
import { resetPostHog, useAnalyticsConsent } from '~/lib/consent'
import type { RouterContext } from '~/lib/router-context'
import { initClientSentry } from '~/lib/sentry-browser'
import type { SessionUser } from '~/server/session.server'
import appCss from '~/styles/app.css?url'

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: `${m.app_name()} - ${m.app_title_suffix()}` },
    ],
    links: [
      { rel: 'stylesheet', href: appCss },
      { rel: 'icon', href: '/favicon.ico', sizes: 'any' },
      { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-32.png' },
      { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
    ],
  }),
  // The signed-in user (or null) is available to every route through the context
  beforeLoad: async () => ({ user: await getSessionUser() }),
  loader: () => getPublicConfig(),
  staleTime: Number.POSITIVE_INFINITY,
  component: RootComponent,
})

function RootComponent() {
  const config = Route.useLoaderData()
  const { user } = Route.useRouteContext()
  useEffect(() => {
    initClientSentry(config.sentryDsn, config.appEnv)
  }, [config.sentryDsn, config.appEnv])

  return (
    <RootDocument user={user}>
      <Outlet />
    </RootDocument>
  )
}

function RootDocument({ children, user }: { children: ReactNode; user: SessionUser | null }) {
  const apiKey = import.meta.env.VITE_POSTHOG_KEY
  const apiHost = import.meta.env.VITE_POSTHOG_HOST

  if (!apiKey || !apiHost) {
    if (import.meta.env.DEV) {
      const missingVariable = apiKey ? 'VITE_POSTHOG_HOST' : 'VITE_POSTHOG_KEY'
      throw new Error(
        `${missingVariable} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once ${missingVariable} is configured`,
      )
    }

    return <DocumentContents>{children}</DocumentContents>
  }

  return (
    <PostHogProvider
      apiKey={apiKey}
      options={{
        api_host: apiHost,
        ui_host: apiHost,
        defaults: '2025-05-24',
        capture_exceptions: true,
        // Nothing is stored or sent before the visitor chooses in the banner (ConsentManager)
        cookieless_mode: 'on_reject',
        disable_session_recording: true,
        debug: import.meta.env.DEV,
        tracing_headers: typeof window !== 'undefined' ? [window.location.hostname] : [],
      }}
    >
      <ConsentManager />
      <PostHogIdentity user={user} />
      <DocumentContents>
        <PostHogErrorBoundary
          fallback={
            <main id="main" role="alert">
              <p>{m.common_something_went_wrong()}</p>
            </main>
          }
        >
          {children}
        </PostHogErrorBoundary>
      </DocumentContents>
    </PostHogProvider>
  )
}

/**
 * Keeps browser events and exception captures attributed to the authenticated session user, once
 * the visitor has accepted analytics. Without that the user is never identified.
 */
function PostHogIdentity({ user }: { user: SessionUser | null }) {
  const posthog = usePostHog()
  const consented = useAnalyticsConsent()
  const identifiedUserId = useRef<string | null>(null)

  useEffect(() => {
    if (!consented) {
      // Withdrawn: applyAnalyticsConsent has already reset PostHog
      identifiedUserId.current = null
      return
    }
    if (!user || identifiedUserId.current === user.id) return

    if (identifiedUserId.current) resetPostHog(posthog)
    posthog.identify(user.id, { email: user.email, name: user.name })
    identifiedUserId.current = user.id
  }, [posthog, user, consented])

  return null
}

function DocumentContents({ children }: { children: ReactNode }) {
  return (
    <html lang={getLocale()}>
      <head>
        <HeadContent />
      </head>
      <body>
        <NewVersionNotice />
        {children}
        <Toaster position="bottom-right" containerAriaLabel={m.common_notifications()} />
        {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-left" />}
        <Scripts />
      </body>
    </html>
  )
}
